async function postJson(endpoint, payload, timeoutMs = 35_000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const requestError = new Error(data?.error || data?.feedback || `Request failed (${response.status}).`);
      requestError.code = data?.diagnostic_code ?? `HTTP_${response.status}`;
      requestError.status = response.status;
      throw requestError;
    }
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('The AI response timed out. Please try again.');
      timeoutError.code = 'UPSTREAM_TIMEOUT';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

const SENTENCE_CACHE_KEY = 'pev-validated-sentence-rounds-v2';
const GUIDED_CACHE_KEY = 'pev-validated-guided-rounds-v5';
const MAX_CACHED_ROUNDS = 12;
const RETRYABLE_GENERATION_CODES = new Set([
  'GEMINI_DUPLICATE_ITEMS',
  'GEMINI_EMPTY_RESPONSE',
  'GEMINI_INVALID_ITEM',
  'GEMINI_INVALID_ITEM_COUNT',
  'GEMINI_INVALID_PASSAGE',
  'GEMINI_INVALID_JSON',
  'GEMINI_CONTEXT_REUSE',
  'GEMINI_IDEA_REUSE',
  'GEMINI_TARGET_MISSING',
  'GEMINI_TARGET_SET_MISMATCH',
  'GEMINI_TIMEOUT',
  'GEMINI_HTTP_429',
  'GEMINI_HTTP_500',
  'GEMINI_HTTP_502',
  'GEMINI_HTTP_503',
  'GEMINI_HTTP_504',
  'UPSTREAM_TIMEOUT',
  'UPSTREAM_UNAVAILABLE',
  'HTTP_429',
  'HTTP_500',
  'HTTP_502',
  'HTTP_503',
  'HTTP_504',
]);

async function requestGeneratedRound(payload) {
  try {
    return await postJson('/api/generate-round', payload, 60_000);
  } catch (error) {
    if (!RETRYABLE_GENERATION_CODES.has(error.code)) throw error;
    return postJson('/api/generate-round', payload, 60_000);
  }
}

function sentenceCacheId(payload) {
  return [payload.theme?.id, payload.subtopic?.id, payload.headword?.id].filter(Boolean).join('::');
}

function guidedCacheId(payload) {
  const targetSignature = (payload.fixed_collocations ?? [])
    .map((target) => `${target.id}:${normalisePhrase(target.canonical_form)}`)
    .join('|');
  return `${sentenceCacheId(payload)}::${targetSignature}`;
}

function readCache(key) {
  try {
    const parsed = JSON.parse(globalThis.localStorage?.getItem(key) || 'null');
    return parsed?.schemaVersion === 1 && Array.isArray(parsed.entries)
      ? parsed
      : { schemaVersion: 1, entries: [] };
  } catch {
    return { schemaVersion: 1, entries: [] };
  }
}

function writeCache(key, cache) {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(cache));
  } catch {
    // A cache failure must never prevent a valid generated round from loading.
  }
}

function readSentenceCache() {
  return readCache(SENTENCE_CACHE_KEY);
}

function writeSentenceCache(cache) {
  writeCache(SENTENCE_CACHE_KEY, cache);
}

function validGuidedItems(items, collocations) {
  if (!Array.isArray(items) || items.length !== collocations.length) return false;
  const ids = items.map((item) => targetIdForItem(item, collocations));
  return ids.every(Boolean)
    && new Set(ids).size === collocations.length
    && items.every((item) => item?.guidedPractice?.collocationChoice?.sentenceWithBlank
      ?.split('{{blank}}').length === 2
      && item.guidedPractice.collocationChoice.modelAnswer?.text
      && item.guidedPractice.collocationChoice.modelAnswer?.textZh);
}

function validGuidedPassage(passage, collocations) {
  if (!passage?.textWithBlanks || !passage?.textZh || !Array.isArray(passage.blanks)
    || passage.blanks.length !== collocations.length) return false;
  const expected = new Set(collocations.map((target) => target.id));
  const returned = passage.blanks.map((blank) => blank.targetId);
  if (new Set(returned).size !== expected.size || returned.some((id) => !expected.has(id))) return false;
  return passage.blanks.every((blank) => blank.answer
    && Array.isArray(blank.acceptedAnswers) && blank.acceptedAnswers.length
    && passage.textWithBlanks.split(blank.token).length === 2);
}

function cacheGuidedRound(payload, result) {
  if (!validGuidedPassage(result.passage, payload.fixed_collocations)) return;
  const full = payload.guided_scope !== 'passage-only';
  if (full && !validGuidedItems(result.items, payload.fixed_collocations)) return;
  const id = guidedCacheId(payload);
  const current = readCache(GUIDED_CACHE_KEY);
  const previous = current.entries.find((entry) => entry.id === id);
  const entry = {
    id,
    savedAt: new Date().toISOString(),
    passage: result.passage,
    items: full ? result.items : previous?.items,
  };
  writeCache(GUIDED_CACHE_KEY, {
    schemaVersion: 1,
    entries: [entry, ...current.entries.filter((value) => value.id !== id)].slice(0, MAX_CACHED_ROUNDS),
  });
}

function cachedGuidedRound(payload) {
  const entry = readCache(GUIDED_CACHE_KEY).entries
    .find((value) => value.id === guidedCacheId(payload));
  if (!entry || !validGuidedPassage(entry.passage, payload.fixed_collocations)) return null;
  if (payload.guided_scope !== 'passage-only'
    && !validGuidedItems(entry.items, payload.fixed_collocations)) return null;
  return {
    status: 'cached',
    mode: payload.mode,
    items: payload.guided_scope === 'passage-only' ? [] : entry.items,
    passage: entry.passage,
  };
}

async function generateGuidedRound(payload) {
  try {
    const result = await requestGeneratedRound(payload);
    const full = payload.guided_scope !== 'passage-only';
    if (!validGuidedPassage(result.passage, payload.fixed_collocations)
      || (full && !validGuidedItems(result.items, payload.fixed_collocations))) {
      const invalid = new Error('The generated Guided Practice round did not pass validation.');
      invalid.code = 'GEMINI_INVALID_PASSAGE';
      throw invalid;
    }
    cacheGuidedRound(payload, result);
    return result;
  } catch (error) {
    const cached = cachedGuidedRound(payload);
    if (cached) return { ...cached, fallbackReason: error.code };
    throw error;
  }
}

function targetIdForItem(item, collocations) {
  if (collocations.some((target) => target.id === item?.sourceTargetId)) return item.sourceTargetId;
  const canonical = String(item?.canonicalForm ?? '').toLocaleLowerCase('en');
  return collocations.find((target) => target.canonical_form.toLocaleLowerCase('en') === canonical)?.id ?? '';
}

function normalisePhrase(value) {
  return String(value ?? '')
    .toLocaleLowerCase('en')
    .replace(/[“”"'‘’.,!?;:()[\]{}—–-]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

function ideaOverlapsTarget(idea, target) {
  const ideaText = normalisePhrase(idea);
  if (!ideaText) return true;
  const accepted = Array.isArray(target?.accepted_variants) && target.accepted_variants.length
    ? target.accepted_variants
    : [target?.canonical_form];
  return accepted.some((variant) => {
    const targetText = normalisePhrase(variant);
    return targetText
      && (` ${ideaText} `.includes(` ${targetText} `) || ` ${targetText} `.includes(` ${ideaText} `));
  });
}

function sentenceItemFailureReason(item, target) {
  const ideas = item?.sentenceBuilding?.ideaPhrases;
  if (ideas?.length !== 2 || !item.sentenceBuilding?.modelAnswer?.text) return 'INVALID_ITEM';
  return ideas.some((idea) => ideaOverlapsTarget(idea.text, target))
    ? 'IDEA_DUPLICATES_TARGET'
    : '';
}

function validSentenceRound(items, collocations) {
  if (!Array.isArray(items) || items.length !== collocations.length) return false;
  const ids = items.map((item) => targetIdForItem(item, collocations));
  return ids.every(Boolean)
    && new Set(ids).size === collocations.length
    && items.every((item, index) => {
      const target = collocations.find((value) => value.id === ids[index]);
      return !sentenceItemFailureReason(item, target);
    });
}

function cacheSentenceRound(payload, items) {
  if (!validSentenceRound(items, payload.fixed_collocations)) return;
  const id = sentenceCacheId(payload);
  const current = readSentenceCache();
  const entry = { id, savedAt: new Date().toISOString(), items };
  writeSentenceCache({
    schemaVersion: 1,
    entries: [entry, ...current.entries.filter((value) => value.id !== id)].slice(0, MAX_CACHED_ROUNDS),
  });
}

function cachedSentenceRound(payload) {
  const entry = readSentenceCache().entries.find((value) => value.id === sentenceCacheId(payload));
  return entry && validSentenceRound(entry.items, payload.fixed_collocations) ? entry.items : null;
}

function reservedIdeaPairs(items) {
  return items.map((item) => item.sentenceBuilding.ideaPhrases.map((idea) => idea.text));
}

async function generateSentenceBuildingRound(payload) {
  const collocations = payload.fixed_collocations;
  const accepted = new Map();
  let pending = collocations;
  let failureReasons = {};

  for (let attempt = 0; attempt <= 2; attempt += 1) {
    const acceptedItems = [...accepted.values()];
    const result = await requestGeneratedRound({
      ...payload,
      fixed_collocations: pending,
      repair_attempt: attempt,
      reserved_idea_pairs: reservedIdeaPairs(acceptedItems),
      repair_reasons: failureReasons,
    });

    const clientFailures = new Map();
    for (const item of result.items ?? []) {
      const targetId = targetIdForItem(item, collocations);
      const target = collocations.find((value) => value.id === targetId);
      if (!target || !pending.some((value) => value.id === targetId)) continue;
      const failureReason = sentenceItemFailureReason(item, target);
      if (failureReason) clientFailures.set(targetId, failureReason);
      else accepted.set(targetId, item);
    }

    if (accepted.size === collocations.length) {
      const items = collocations.map((target) => accepted.get(target.id));
      cacheSentenceRound(payload, items);
      return { status: 'generated', mode: payload.mode, items, repairAttempts: attempt };
    }

    const returnedFailures = new Map(
      (result.failures ?? []).map((failure) => [failure.target_id, failure.reason]),
    );
    for (const [targetId, reason] of clientFailures) returnedFailures.set(targetId, reason);
    pending = collocations.filter((target) => !accepted.has(target.id));
    failureReasons = Object.fromEntries(
      pending.map((target) => [target.id, returnedFailures.get(target.id) ?? 'MISSING_ITEM']),
    );
  }

  const error = new Error('The AI could not repair every Sentence Building item. Please try again.');
  error.code = 'SENTENCE_REPAIR_EXHAUSTED';
  throw error;
}

export function checkResponse(payload) {
  return postJson('/api/check-response', payload, 30_000);
}

export async function generateRound(payload) {
  if (payload.mode === 'guided-practice') return generateGuidedRound(payload);
  if (payload.mode !== 'sentence-building') return requestGeneratedRound(payload);
  try {
    return await generateSentenceBuildingRound(payload);
  } catch (error) {
    const cached = cachedSentenceRound(payload);
    if (cached) return { status: 'cached', mode: payload.mode, items: cached, fallbackReason: error.code };
    throw error;
  }
}
